import React, { useEffect, useState } from "react";
import { DefaultValues, useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { CalendarIcon } from "lucide-react";
import { format } from "date-fns";

import { useInjection } from "@di/DependencyContext";
import { DependencyType } from "@di/DependencyType";
import { SerialRangeService } from "@modules/serial-range/serial-range.service";
import { LoadingState } from "@util/loadingstate.util";
import { ParsedError, parseServerError } from "@util/parseError";
import { ProductType, SerialRange } from "generated/graphql";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "~/components/ui/form";
import { Input } from "~/components/ui/input";
import { Textarea } from "~/components/ui/textarea";
import { Button } from "~/components/ui/button";
import {
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { Calendar } from "~/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "~/components/ui/popover";
import { Spinner } from "~/components/ui/spinner";
import { cn } from "~/lib/utils";

export interface SerialRangeFormProps {
  onClose: () => void;
  onSubmit: () => void;
  productTypes: ProductType[];
  serialRange?: SerialRange;
}

export const BUILD_END_DATE_AFTER_START_ERROR =
  "Build end date must be after build start date";

export const serialRangeFormSchema = yup.object({
  // Regex for YYMMDD formatted date string
  startNumber: yup
    .string()
    .required("Range start is required")
    .matches(/^\d{1,7}$/, "Enter up to 7 digits"),
  endNumber: yup
    .string()
    .required("Range end is required")
    .matches(/^\d{1,7}$/, "Enter up to 7 digits")
    .test(
      "greater-than-start",
      "End must be greater than start number",
      function (value) {
        const { startNumber } = this.parent as { startNumber?: string };
        if (!value || !startNumber) {
          return false;
        }
        return Number(value) > Number(startNumber);
      }
    ),
  buildStartDate: yup
    .date()
    .typeError("Build start date is required")
    .required("Build start date is required"),
  buildEndDate: yup
    .date()
    .nullable()
    .transform((value, originalValue) => {
      if (!originalValue) {
        return null;
      }
      return value;
    })
    .test(
      "after-build-start-date",
      BUILD_END_DATE_AFTER_START_ERROR,
      function (value) {
        const { buildStartDate } = this.parent as { buildStartDate?: Date };
        if (!value || !buildStartDate) {
          return true;
        }

        return value > buildStartDate;
      }
    )
    .optional(),
  buildDescription: yup.string().required("Build description is required"),
  firmwareVersion: yup.string().optional(),
  notes: yup.string().optional(),
  productTypeId: yup.string().required("Product type is required"),
});
type SerialFormValues = yup.InferType<typeof serialRangeFormSchema>;

const getSerialRangeFormDefaults = (
  serialRange?: SerialRange
): DefaultValues<SerialFormValues> => ({
  startNumber:
    serialRange?.startNumber !== undefined
      ? serialRange.startNumber.toString()
      : "",
  endNumber:
    serialRange?.endNumber !== undefined
      ? serialRange.endNumber.toString()
      : "",
  buildStartDate: serialRange?.buildStartDate
    ? new Date(serialRange.buildStartDate)
    : undefined,
  buildEndDate: serialRange?.buildEndDate
    ? new Date(serialRange.buildEndDate)
    : null,
  buildDescription: serialRange?.buildDescription ?? "",
  firmwareVersion: serialRange?.firmwareVersion ?? "",
  notes: serialRange?.notes ?? "",
  productTypeId: serialRange?.productType?.id ?? "",
});

export const SerialRangeForm: React.FC<SerialRangeFormProps> = (props) => {
  const isEditing = Boolean(props.serialRange);
  const form = useForm<SerialFormValues>({
    resolver: yupResolver(serialRangeFormSchema),
    defaultValues: getSerialRangeFormDefaults(),
  });
  const serialRangeService = useInjection<SerialRangeService>(
    DependencyType.SerialRangeService
  );
  const [loadingState, setLoadingState] = useState<LoadingState>(
    LoadingState.Idle
  );
  const [error, setError] = useState<ParsedError>();
  const buildStartDate = form.watch("buildStartDate");
  const buildEndDate = form.watch("buildEndDate");

  useEffect(() => {
    if (
      buildStartDate &&
      buildEndDate &&
      buildEndDate.getTime() <= buildStartDate.getTime()
    ) {
      form.setValue("buildEndDate", null, {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
  }, [buildEndDate, buildStartDate, form]);

  useEffect(() => {
    form.reset(getSerialRangeFormDefaults(props.serialRange));
    setError(undefined);
    setLoadingState(LoadingState.Idle);
  }, [form, props.serialRange]);

  const onSubmit = async (data: SerialFormValues) => {
    try {
      setError(undefined);
      setLoadingState(LoadingState.Loading);
      if (props.serialRange) {
        await serialRangeService.updateSerialRange({
          id: props.serialRange.id,
          startNumber: Number(data.startNumber),
          endNumber: Number(data.endNumber),
          buildStartDate: data.buildStartDate.toISOString(),
          buildEndDate: data.buildEndDate
            ? data.buildEndDate.toISOString()
            : null,
          buildDescription: data.buildDescription,
          firmwareVersion: data.firmwareVersion || null,
          notes: data.notes || null,
          productTypeId: data.productTypeId,
        });
      } else {
        await serialRangeService.createSerialRange({
          startNumber: Number(data.startNumber),
          endNumber: Number(data.endNumber),
          buildStartDate: data.buildStartDate.toISOString(),
          buildEndDate: data.buildEndDate
            ? data.buildEndDate.toISOString()
            : undefined,
          buildDescription: data.buildDescription,
          firmwareVersion: data.firmwareVersion || undefined,
          notes: data.notes || undefined,
          productTypeId: data.productTypeId,
        });
      }
      setLoadingState(LoadingState.Loaded);
      props.onSubmit();
      form.reset();
      props.onClose();
    } catch (e) {
      const parsed = parseServerError(e);
      console.error(parsed);
      setError(parsed);
      setLoadingState(LoadingState.Error);
    }
  };

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="flex flex-col gap-6"
      >
        <DialogHeader className="gap-3 text-left">
          <DialogTitle>
            {isEditing
              ? "Edit serial number range"
              : "Create new serial number range"}
          </DialogTitle>
          <DialogDescription>
            Serial number ranges denote groupings of PES products and are
            associated with a single product type.
            {!isEditing &&
              " New ranges cannot overlap with any existing range."}
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 sm:grid-cols-2">
          <FormField
            control={form.control}
            name="startNumber"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Range start</FormLabel>
                <FormControl>
                  <Input
                    name={field.name}
                    ref={field.ref}
                    value={
                      field.value && field.value.length > 0
                        ? field.value.padStart(7, "0")
                        : ""
                    }
                    inputMode="numeric"
                    placeholder="0000001"
                    onChange={(event) => {
                      const digits = event.target.value
                        .replace(/\D/g, "")
                        .slice(-7);
                      field.onChange(digits);
                    }}
                    onBlur={field.onBlur}
                    required
                  />
                </FormControl>
                <FormDescription>Up to 7 digits</FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="endNumber"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Range end</FormLabel>
                <FormControl>
                  <Input
                    name={field.name}
                    ref={field.ref}
                    value={
                      field.value && field.value.length > 0
                        ? field.value.padStart(7, "0")
                        : ""
                    }
                    inputMode="numeric"
                    placeholder="0001000"
                    onChange={(event) => {
                      const digits = event.target.value
                        .replace(/\D/g, "")
                        .slice(-7);
                      field.onChange(digits);
                    }}
                    onBlur={field.onBlur}
                    required
                  />
                </FormControl>
                <FormDescription>
                  Up to 7 digits; greater than start number
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="buildStartDate"
            render={({ field }) => (
              <FormItem className="flex flex-col">
                <FormLabel>Build start date</FormLabel>
                <Popover>
                  <PopoverTrigger>
                    <FormControl>
                      <Button
                        type="button"
                        variant="outline"
                        className={cn(
                          "w-full justify-start text-left font-normal",
                          !field.value && "text-muted-foreground"
                        )}
                      >
                        <CalendarIcon className="mr-2 size-4" />
                        {field.value
                          ? format(field.value, "yyyy-MM-dd")
                          : "Select date"}
                      </Button>
                    </FormControl>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={field.value ?? undefined}
                      onSelect={(date) => field.onChange(date)}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="buildEndDate"
            render={({ field }) => (
              <FormItem className="flex flex-col">
                <FormLabel>Build end date</FormLabel>
                <div className="flex items-center gap-2">
                  <Popover>
                    <PopoverTrigger>
                      <FormControl>
                        <Button
                          type="button"
                          variant="outline"
                          className={cn(
                            "w-full justify-start text-left font-normal",
                            !field.value && "text-muted-foreground"
                          )}
                        >
                          <CalendarIcon className="mr-2 size-4" />
                          {field.value
                            ? format(field.value, "yyyy-MM-dd")
                            : "Select date"}
                        </Button>
                      </FormControl>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={field.value ?? undefined}
                        disabled={(date) => {
                          if (!buildStartDate) {
                            return false;
                          }

                          return date.getTime() <= buildStartDate.getTime();
                        }}
                        onSelect={(date) => field.onChange(date ?? null)}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                  {field.value && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => field.onChange(null)}
                      className="px-2 text-xs text-muted-foreground hover:text-foreground"
                    >
                      Clear
                    </Button>
                  )}
                </div>
                <FormDescription>Optional</FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="productTypeId"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Product type</FormLabel>
                <Select
                  onValueChange={field.onChange}
                  value={field.value ?? ""}
                  disabled={props.productTypes.length === 0}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select a product type" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {props.productTypes.map((productType) => (
                      <SelectItem key={productType.id} value={productType.id}>
                        {productType.name} - {productType.code}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="firmwareVersion"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Firmware version</FormLabel>
                <FormControl>
                  <Input {...field} value={field.value ?? ""} placeholder="1" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="buildDescription"
            render={({ field }) => (
              <FormItem className="sm:col-span-2">
                <FormLabel>Build description/number</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    value={field.value ?? ""}
                    placeholder="Build #1"
                    required
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="notes"
            render={({ field }) => (
              <FormItem className="sm:col-span-2">
                <FormLabel>Notes</FormLabel>
                <FormControl>
                  <Textarea
                    {...field}
                    value={field.value ?? ""}
                    placeholder="First batch of 80 cassettes"
                    rows={3}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        {loadingState === LoadingState.Error && error && (
          <p className="text-sm text-destructive">{error.message}</p>
        )}

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              form.reset();
              props.onClose();
            }}
            disabled={loadingState === LoadingState.Loading}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={loadingState === LoadingState.Loading}
          >
            {loadingState === LoadingState.Loading && (
              <Spinner className="mr-2 size-4" />
            )}
            Save
          </Button>
        </DialogFooter>
      </form>
    </Form>
  );
};
